import { AlertTriangle, Check, CheckCircle2, CircleSlash } from 'lucide-react';
import {
  useAcknowledgeMigrationReport,
  useMigrationReports,
  type MigrationReport,
} from '@/hooks/useNotifications.ts';
import { Badge } from '@/components/ui/badge.tsx';
import { Button } from '@/components/ui/button.tsx';

/**
 * What the actions migration did, per alert definition, with an acknowledgement.
 *
 * The detail behind `MigrationReportBanner`. Three outcomes, and they are not
 * variations on a theme — each asks something different of the reader:
 *
 * - `migrated` — a route now does what the action did. Nothing to do but agree.
 * - `needs_manual_setup` — the action could not be expressed as a route, so **nobody
 *   is being paged for it right now**. This is the row the whole surface exists for.
 * - `dropped_never_delivered` — the action was already broken before the migration
 *   (§1.1: `send_slack` never worked). Worth saying plainly, because "we removed it"
 *   and "it never ran" invite very different reactions.
 */

const OUTCOMES: Record<string, { label: string; hint: string; tone: 'ok' | 'warn' | 'muted' }> = {
  migrated: {
    label: 'Converted',
    hint: 'A notification route now does what this action did.',
    tone: 'ok',
  },
  needs_manual_setup: {
    label: 'Needs setting up',
    hint: 'This could not be converted automatically — nothing is being sent for it until you add a route.',
    tone: 'warn',
  },
  dropped_never_delivered: {
    label: 'Removed — never delivered',
    hint: 'This action had never sent anything, so removing it changed nothing.',
    tone: 'muted',
  },
};

function ReportRow({ report }: { report: MigrationReport }) {
  const acknowledge = useAcknowledgeMigrationReport();
  const outcome = OUTCOMES[report.outcome] ?? {
    label: report.outcome,
    hint: '',
    tone: 'muted' as const,
  };

  const Icon = outcome.tone === 'ok' ? CheckCircle2 : outcome.tone === 'warn' ? AlertTriangle : CircleSlash;

  return (
    <div className="rounded-md border border-border-subtle px-3 py-2 space-y-1.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* A div, not a p: Badge renders a div, and a div inside a p is invalid
              markup that React reports as a hydration error. */}
          <div className="text-sm truncate flex items-center gap-2">
            <Icon
              className={`h-3.5 w-3.5 shrink-0 ${outcome.tone === 'warn' ? 'text-sev-3' : 'text-muted-foreground'}`}
            />
            {report.alertDefinitionName}
            <Badge className="bg-muted text-muted-foreground border-0 font-normal">{report.actionType}</Badge>
          </div>
          <p className="text-xs text-muted-foreground">{outcome.hint}</p>
        </div>
        <span className="flex items-center gap-2 shrink-0">
          <Badge variant={outcome.tone === 'warn' ? 'outline' : 'secondary'} className="font-normal">
            {outcome.label}
          </Badge>
          {report.acknowledgedAt ? (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Check className="h-3.5 w-3.5" /> Reviewed
            </span>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => { acknowledge.mutate(report.id); }}
              disabled={acknowledge.isPending}
            >
              {acknowledge.isPending ? 'Saving…' : 'Mark reviewed'}
            </Button>
          )}
        </span>
      </div>

      {/* The caveats are the actionable half — "this had an Authorization header you
          must re-add by hand". The API has already redacted anything that grants
          access, so what is left is instructions. */}
      {report.caveats.length > 0 ? (
        <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-0.5">
          {report.caveats.map((caveat) => (
            <li key={caveat}>{caveat}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function MigrationReportList({ enabled }: { enabled: boolean }) {
  const { data } = useMigrationReports(enabled);

  // Nothing to say when no migration has run here — which is every organization that
  // never had a channel action, and is the normal case rather than an error.
  if (!data || data.reports.length === 0) return null;

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Converted from alert actions
      </h3>
      <p className="text-xs text-muted-foreground">
        Delivery used to be configured on each alert definition. These were rewritten as routes during an upgrade —
        this is the record of what happened to each one, so you can confirm the right people still get paged.
      </p>
      {data.reports.map((report) => (
        <ReportRow key={report.id} report={report} />
      ))}
    </div>
  );
}
